import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'preact/hooks'

import {
  createSensitivityFlag,
  deleteSensitivityFlag,
  fetchSensitivityFlags,
  fetchUserSettings,
  updateUserSettings,
} from '../state/api'
import { auth } from '../state/auth'
import { type SaveStatus, SaveStatusIndicator } from './SaveStatusIndicator'
import { SettingsSection } from './SettingsSection'
import './MealPreferencesSettings.css'

interface MealSlot {
  name: string
  default_hour: number
}

export function MealPreferencesSettings() {
  const isLoggedIn = auth.value.token
  const queryClient = useQueryClient()

  const { data: settings, isLoading: settingsLoading } = useQuery({
    enabled: !!isLoggedIn,
    queryFn: fetchUserSettings,
    queryKey: ['userSettings'],
  })

  const { data: flags = [], isLoading: flagsLoading } = useQuery({
    enabled: !!isLoggedIn,
    queryFn: fetchSensitivityFlags,
    queryKey: ['sensitivityFlags'],
  })
  const isLoading = settingsLoading || flagsLoading

  const [newFlagName, setNewFlagName] = useState('')
  const [newSlotName, setNewSlotName] = useState('')
  const [newSlotHour, setNewSlotHour] = useState('12')
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ status: 'idle' })

  const currentSlots: MealSlot[] = settings?.meal_slots ?? []
  const currentFlagNames: string[] = flags.map((f) => f.name)

  // Save immediately on any change (settings — meal slots only now; flags
  // are managed via the dedicated /sensitivity-flags endpoint).
  const saveMutation = useMutation({
    mutationFn: updateUserSettings,
    onSuccess: (result) => {
      queryClient.setQueryData(['userSettings'], result)
      setSaveStatus({ status: 'saved', time: new Date() })
    },
    onError: (err) => {
      setSaveStatus({
        error: err instanceof Error ? err.message : 'Failed to save',
        status: 'error',
      })
    },
  })

  const flagSaveSettled = (err?: unknown) => {
    if (err) {
      setSaveStatus({
        error: err instanceof Error ? err.message : 'Failed to save',
        status: 'error',
      })
    } else {
      setSaveStatus({ status: 'saved', time: new Date() })
    }
    queryClient.invalidateQueries({ queryKey: ['sensitivityFlags'] })
  }

  const addFlagMutation = useMutation({
    mutationFn: (name: string) => createSensitivityFlag({ name, sort_order: flags.length }),
    onError: (err) => flagSaveSettled(err),
    onSuccess: () => flagSaveSettled(),
  })

  const deleteFlagMutation = useMutation({
    mutationFn: deleteSensitivityFlag,
    onError: (err) => flagSaveSettled(err),
    onSuccess: () => flagSaveSettled(),
  })

  const saveSlots = (slots: MealSlot[]) => {
    setSaveStatus({ status: 'saving' })
    saveMutation.mutate({ meal_slots: slots })
  }

  // Sensitivity flags — write through the dedicated endpoint
  const addFlag = () => {
    const trimmed = newFlagName.trim()
    if (!trimmed || currentFlagNames.includes(trimmed)) return
    setSaveStatus({ status: 'saving' })
    addFlagMutation.mutate(trimmed)
    setNewFlagName('')
  }

  const removeFlag = (name: string) => {
    const flag = flags.find((f) => f.name === name)
    if (!flag) return
    setSaveStatus({ status: 'saving' })
    deleteFlagMutation.mutate(flag.id)
  }

  // Meal slots — save on each action
  const addSlot = () => {
    const trimmed = newSlotName.trim()
    const hour = parseInt(newSlotHour, 10)
    if (!trimmed || isNaN(hour) || hour < 0 || hour > 23) return
    if (currentSlots.some((s) => s.name.toLowerCase() === trimmed.toLowerCase())) return
    saveSlots([...currentSlots, { name: trimmed, default_hour: hour }])
    setNewSlotName('')
    setNewSlotHour('12')
  }

  const removeSlot = (name: string) => {
    saveSlots(currentSlots.filter((s) => s.name !== name))
  }

  const updateSlotHour = (name: string, hour: number) => {
    if (isNaN(hour) || hour < 0 || hour > 23) return
    saveSlots(currentSlots.map((s) => (s.name === name ? { ...s, default_hour: hour } : s)))
  }

  return (
    <SettingsSection
      title="Meal Preferences"
      description="Configure meal slots and meal flags for quick meal logging."
      isLoading={isLoading}
      headerExtra={<SaveStatusIndicator state={saveStatus} />}
    >
      <p class="subsection-desc">
        <a href="/food-items">Browse food item library</a>
      </p>

      {/* Meal Flags */}
      <div class="meal-pref-subsection">
        <h3>Meal Flags</h3>
        <p class="subsection-desc">Flags for categorizing meals (e.g., gluten, dairy, keto, cheat day).</p>

        <div class="area-list">
          {currentFlagNames.map((flagName) => (
            <div key={flagName} class="area-chip">
              <span>{flagName}</span>
              <button
                type="button"
                class="chip-remove"
                onClick={() => removeFlag(flagName)}
                aria-label={`Remove ${flagName}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        <div class="add-row">
          <input
            type="text"
            placeholder="Add meal flag..."
            value={newFlagName}
            onInput={(e) => setNewFlagName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Enter' && addFlag()}
          />
          <button type="button" class="btn-secondary" onClick={addFlag}>
            Add
          </button>
        </div>
      </div>

      {/* Meal Slots */}
      <div class="meal-pref-subsection">
        <h3>Meal Slots</h3>
        <p class="subsection-desc">Define your typical meal times for the quick-log UI.</p>

        <div class="slots-list">
          {[...currentSlots]
            .sort((a, b) => a.default_hour - b.default_hour)
            .map((slot) => (
              <div key={slot.name} class="slot-row">
                <span class="slot-label">{slot.name}</span>
                <label class="slot-hour-label">
                  Default hour:
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={slot.default_hour}
                    onChange={(e) =>
                      updateSlotHour(slot.name, parseInt((e.target as HTMLInputElement).value, 10))
                    }
                    class="slot-hour-input"
                  />
                </label>
                <button type="button" class="btn-danger-small" onClick={() => removeSlot(slot.name)}>
                  Remove
                </button>
              </div>
            ))}
        </div>

        <div class="add-row">
          <input
            type="text"
            placeholder="Slot name (e.g., Breakfast)"
            value={newSlotName}
            onInput={(e) => setNewSlotName((e.target as HTMLInputElement).value)}
          />
          <input
            type="number"
            min="0"
            max="23"
            placeholder="Hour"
            value={newSlotHour}
            onInput={(e) => setNewSlotHour((e.target as HTMLInputElement).value)}
            class="slot-hour-input"
          />
          <button type="button" class="btn-secondary" onClick={addSlot}>
            Add
          </button>
        </div>
      </div>
    </SettingsSection>
  )
}
